import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import {
  Alert,
  Badge,
  Button,
  Chip,
  ConfirmDialog,
  DataTable,
  Drawer,
  DrawerFooter,
  ErrorBanner,
  Field,
  FieldGrid,
  LoadingButton,
  SearchCombobox,
  TextInput,
  useToast,
  type DataTableColumn,
  type ImpactItem,
} from '@zarya/ui';
import type { ApiError } from '@app/api/ApiError';
import { useCanOperar } from '@app/auth/useCan';
import { dataHour } from '@app/format';
import { t } from '@app/i18n';
import { identityAccessHref } from '@app/navigation';
import { OwnerCombobox } from '@app/shared/OwnerCombobox';
import { useIdentities } from '../Contracts/Contracts.api';
import { stateLabel } from '../Identities';
import { useMultiSite } from '../Sites/Sites.api';
import {
  useDeleteResource,
  useRelinkAgent,
  useResource,
  useResourceImpact,
  useSyncAgents,
  useUpdateResource,
  useUsersForOwner,
  type AgentInvoker,
  type Resource,
  type ResourceAgent,
} from './Resources.api';
import { apiOf, cardErrorText, removalText } from './AgentModel';
import { agentPath, agentState, kind as kindBadge } from './resourceVocab';
import s from './Resources.module.css';

/**
 * T7 — o detalhe de um **recurso agente** (fase 2, aim-co1.3; protótipo aprovado em 03/10, pranchas
 * `Detalhe-agente-cluster`, `Detalhe-agente-externo`, `Agente-sem-destino-confiavel` e `Rota-faltando`).
 *
 * O agente não tem cadastro no console (decisão 21): o gateway é a declaração. Todo agente vem de uma API
 * `a2a/<caminho>` da policy global do gateway: o interno, de uma que aponta para um Service do cluster; o externo, de
 * uma que aponta para fora (aim-cpj, F2: a borda saiu). Por isso esta gaveta só edita o que é do produto — o nome e o
 * dono — e tudo o mais é leitura, com «Reler o card» e «Conferir de novo» pedindo ao executor uma leitura nova do
 * gateway.
 *
 * Os estados mudam o que a gaveta oferece:
 *  - **sem rota** (interno): o aviso de que o gateway não tem a API, com «Conferir de novo» e «Remover». O console
 *    não escreve no gateway (decisão 9), e a sugestão de rota da borda saiu sem substituto (D6, decisão A do M2);
 *  - **sem destino confiável**: as três saídas da D8 — religar, converter em externo, remover;
 *  - **externo que pede credencial**: onde configurá-la no gateway. O externo não tem «Remover»: ele sai quando o
 *    cliente apaga a API.
 */

type TabID = 'general' | 'skills' | 'invokers';

interface AgentFormValues {
  name: string;
  /** `Nome <e-mail>`; vazio = sem dono (a forma do `OwnerCombobox`). */
  owner: string;
}

export interface AgentDrawerProps {
  resourceId: string;
  onClose: () => void;
}

export function AgentDrawer({ resourceId, onClose }: AgentDrawerProps) {
  const resource = useResource(resourceId);
  const toast = useToast();
  const multiSite = useMultiSite();
  const canSave = useCanOperar('updateResource');
  const canSync = useCanOperar('syncAgentResources');
  const canRelink = useCanOperar('relinkAgentResource');
  const canDelete = useCanOperar('deleteResource');
  const usuarios = useUsersForOwner(canSave);
  const save = useUpdateResource(resourceId);
  const sync = useSyncAgents();
  const relink = useRelinkAgent(resourceId);
  const remove = useDeleteResource(resourceId);

  const [tab, setTab] = useState<TabID>('general');
  const [saveError, setSaveError] = useState<ApiError | null>(null);
  const [syncError, setSyncError] = useState<ApiError | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState<ApiError | null>(null);
  const impact = useResourceImpact(resourceId, null, removing);

  const { control, handleSubmit, reset, formState } = useForm<AgentFormValues>({
    defaultValues: { name: '', owner: '' },
  });
  useEffect(() => {
    if (resource.data) reset({ name: resource.data.name, owner: resource.data.owner ?? '' });
  }, [resource.data, reset]);

  const data = resource.data;
  const agent = data?.agent ?? null;
  const name = data?.name ?? '';

  const send = handleSubmit(async (values) => {
    setSaveError(null);
    try {
      const saved = await save.mutateAsync({
        version: data?.version,
        name: values.name,
        owner: values.owner || null,
      });
      reset({ name: saved.name, owner: saved.owner ?? '' });
      toast.mostrar(t('resources.agent.saved', { name: saved.name }), { tone: 'sucesso' });
    } catch (error) {
      setSaveError(error as ApiError);
    }
  });

  /**
   * «Reler o card», «Conferir de novo» e «Converter em agente externo» são a mesma leitura do gateway do site. O
   * que muda é a frase depois: converter diz se a API já aponta para fora do cluster.
   */
  async function readGatewayAgain(after?: (updated: Resource | undefined) => void) {
    if (!data) return;
    setSyncError(null);
    setNotice(null);
    try {
      const result = await sync.mutateAsync(data.site_id);
      const updated = result.resources.find((item) => item.id === data.id);
      const fresh = await resource.refetch();
      if (after) {
        after(fresh.data ?? updated);
        return;
      }
      toast.mostrar(
        t('resources.agent.card.reread.done', { created: result.created, updated: result.updated }),
        { tone: 'sucesso' },
      );
    } catch (error) {
      setSyncError(error as ApiError);
    }
  }

  async function confirmRemoval() {
    setRemoveError(null);
    try {
      await remove.mutateAsync();
      setRemoving(false);
      toast.mostrar(t('resources.drawer.removed', { name }), { tone: 'sucesso' });
      onClose();
    } catch (error) {
      setRemoveError(error as ApiError);
    }
  }

  const invokers = agent?.invokers ?? [];
  const skills = agent?.card?.skills ?? [];
  const state = agent ? agentState(agent.state) : null;
  const credentialMissing =
    agent?.scope === 'external' &&
    (agent.card?.security_schemes?.length ?? 0) > 0 &&
    agent.route?.credential_configured === false;

  const impactItems: ImpactItem[] = [
    { label: t('resources.drawer.impact.contracts'), count: impact.data?.contracts ?? 0 },
    { label: t('resources.drawer.impact.policies'), count: impact.data?.policies ?? 0 },
  ];

  return (
    <Drawer
      open
      title={name || t('resources.drawer.loading')}
      subtitle={
        data && agent ? (
          <span className={s.drawerBadges}>
            <Badge tone={kindBadge('agent').tone}>{kindBadge('agent').rotulo}</Badge>
            {agent.scope === 'external' ? (
              <Badge tone="neutral">{t('resources.agent.badge.external')}</Badge>
            ) : null}
            {state ? <Badge tone={state.tone}>{state.rotulo}</Badge> : null}
            {credentialMissing ? (
              <Badge tone="warning">{t('resources.agent.badge.credentialMissing')}</Badge>
            ) : null}
            {multiSite ? <Chip>{data.site_slug}</Chip> : null}
          </span>
        ) : undefined
      }
      dirty={formState.isDirty}
      onClose={onClose}
      footer={
        <DrawerFooter>
          <Button variant="out" onClick={onClose}>
            {t('resources.drawer.footer.cancel')}
          </Button>
          {canSave ? (
            <LoadingButton
              variant="primary"
              loading={save.isPending}
              loadingLabel={t('resources.drawer.footer.save.loading')}
              onClick={() => void send()}
            >
              {t('resources.drawer.footer.save')}
            </LoadingButton>
          ) : null}
        </DrawerFooter>
      }
    >
      {resource.isLoading ? <p className={s.carregando}>{t('resources.drawer.loading')}</p> : null}
      {resource.isError ? (
        <ErrorBanner
          error={resource.error}
          title={t('resources.drawer.error.open')}
          onRetry={() => void resource.refetch()}
          hideLink
        />
      ) : null}
      {saveError ? (
        <ErrorBanner
          error={saveError}
          escrita
          title={t('resources.drawer.error.save')}
          onRetry={() => setSaveError(null)}
          retryLabel={t('resources.drawer.error.close')}
        />
      ) : null}
      {syncError ? (
        <ErrorBanner
          error={syncError}
          escrita
          title={t('resources.agent.card.reread.error')}
          onRetry={() => setSyncError(null)}
          retryLabel={t('resources.drawer.error.close')}
        />
      ) : null}
      {notice ? (
        <Alert tone="info" role="status">
          {notice}
        </Alert>
      ) : null}

      {data && agent ? (
        <>
          <p className={s.agentIntro}>
            {agent.scope === 'external'
              ? t('resources.agent.subtitle.external', {
                  when: dataHour(data.created_at) ?? '—',
                  api: apiOf(agent),
                })
              : t('resources.agent.subtitle.internal', { api: apiOf(agent) })}
          </p>

          <StateAlerts
            resource={data}
            agent={agent}
            canSync={canSync}
            canRelink={canRelink}
            canDelete={canDelete}
            syncing={sync.isPending}
            relinking={relink.isPending}
            onRecheck={() => void readGatewayAgain()}
            onConvert={() =>
              void readGatewayAgain((updated) => {
                if (updated?.agent?.scope === 'external') {
                  toast.mostrar(t('resources.agent.convert.done', { name }), { tone: 'sucesso' });
                } else {
                  setNotice(t('resources.agent.convert.pending', { api: apiOf(agent) }));
                }
              })
            }
            onRelink={async (identityId, slug) => {
              await relink.mutateAsync(identityId);
              toast.mostrar(t('resources.agent.relink.done', { name, slug }), { tone: 'sucesso' });
            }}
            onRemove={() => setRemoving(true)}
          />

          <div className={s.abas} role="tablist" aria-label={t('resources.agent.tabs.label')}>
            {(
              [
                ['general', t('resources.agent.tab.general')],
                ['skills', t('resources.agent.tab.skills', { n: skills.length })],
                ['invokers', t('resources.agent.tab.invokers', { n: invokers.length })],
              ] as const
            ).map(([id, label]) => (
              <button
                key={id}
                type="button"
                role="tab"
                data-testid={`agent-tab-${id}`}
                aria-selected={tab === id}
                className={[s.aba, tab === id ? s.abaOn : ''].filter(Boolean).join(' ')}
                onClick={() => setTab(id)}
              >
                {label}
              </button>
            ))}
          </div>

          {tab === 'general' ? (
            <form
              onSubmit={(event) => {
                event.preventDefault();
                void send();
              }}
            >
              <h3 className={s.sectionTitle}>{t('resources.agent.section.resource')}</h3>
              <FieldGrid>
                <Field label={t('resources.agent.field.path')} span={12} help={t('resources.agent.field.path.help')}>
                  {(props) => <TextInput {...props} mono readOnly value={agentPath(agent.path)} />}
                </Field>
                {agent.scope === 'external' ? (
                  <Field label={t('resources.agent.field.origin')} span={12}>
                    {(props) => (
                      <TextInput
                        {...props}
                        readOnly
                        value={t('resources.agent.field.origin.value', { api: apiOf(agent) })}
                      />
                    )}
                  </Field>
                ) : null}
                <Field label={t('resources.agent.field.name')} span={6}>
                  {(props) => (
                    <Controller
                      control={control}
                      name="name"
                      render={({ field }) => (
                        <TextInput {...props} {...field} readOnly={!canSave} />
                      )}
                    />
                  )}
                </Field>
                <Field label={t('resources.agent.field.owner')} span={6} required>
                  {(props) => (
                    <Controller
                      control={control}
                      name="owner"
                      render={({ field }) => (
                        <OwnerCombobox
                          id={props.id}
                          aria-describedby={props['aria-describedby']}
                          users={usuarios.data ?? []}
                          value={field.value}
                          onChange={field.onChange}
                          disabled={!canSave}
                        />
                      )}
                    />
                  )}
                </Field>
              </FieldGrid>
              <dl className={s.readout}>
                <dt>{t('resources.agent.field.description')}</dt>
                <dd>
                  {agent.card?.description ? (
                    <>
                      {agent.card.description}{' '}
                      <span className={s.sub}>{t('resources.agent.field.description.fromCard')}</span>
                    </>
                  ) : (
                    <span className={s.vazio}>—</span>
                  )}
                </dd>
                {multiSite ? (
                  <>
                    <dt>{t('resources.agent.field.site')}</dt>
                    <dd>
                      {t(
                        agent.scope === 'external'
                          ? 'resources.agent.field.site.external'
                          : 'resources.agent.field.site.internal',
                        { site: data.site_slug },
                      )}
                    </dd>
                  </>
                ) : null}
                <dt>{t('resources.agent.field.binding')}</dt>
                <dd>
                  <BindingDetail agent={agent} />
                </dd>
              </dl>

              <CardSection
                agent={agent}
                canSync={canSync}
                syncing={sync.isPending}
                onReread={() => void readGatewayAgain()}
              />
              <RouteSection agent={agent} />
            </form>
          ) : null}

          {tab === 'skills' ? <SkillsTab agent={agent} /> : null}
          {tab === 'invokers' ? <InvokersTab invokers={invokers} multiSite={multiSite} /> : null}
        </>
      ) : null}

      <ConfirmDialog
        open={removing}
        destructive
        title={t('resources.drawer.remove.resourceTitle', { name })}
        message={
          impact.data ? (
            <p>{removalText(name, impact.data.contracts)}</p>
          ) : impact.isError ? (
            <p>{t('resources.drawer.remove.impactRetry')}</p>
          ) : (
            <p>{t('resources.drawer.remove.impactLoading')}</p>
          )
        }
        impact={impact.data ? impactItems : undefined}
        confirmLabel={t('resources.drawer.remove.confirmResource')}
        loading={remove.isPending}
        confirmDisabled={!impact.data}
        error={
          removeError ? (
            <ErrorBanner error={removeError} escrita title={t('resources.drawer.remove.error')} />
          ) : impact.isError ? (
            <ErrorBanner
              error={impact.error}
              title={t('resources.drawer.remove.impactError')}
              onRetry={() => void impact.refetch()}
              hideLink
            />
          ) : null
        }
        onConfirm={() => void confirmRemoval()}
        onCancel={() => {
          setRemoving(false);
          setRemoveError(null);
        }}
      />
    </Drawer>
  );
}

/** «Ligação» do detalhe: a identidade (com o contrato e o estado dela), o externo, ou a identidade perdida. */
function BindingDetail({ agent }: { agent: ResourceAgent }) {
  const identities = useIdentities();
  if (agent.scope === 'external') return <>{t('resources.agent.binding.external')}</>;
  if (agent.identity_id && agent.identity_slug) {
    const identity = identities.data?.find((item) => item.id === agent.identity_id);
    const parts = [t('resources.agent.binding.identity', { slug: agent.identity_slug })];
    if (identity) {
      parts.push(
        identity.active_contract_version
          ? t('resources.agent.binding.contract', { version: identity.active_contract_version })
          : t('resources.agent.binding.noContract'),
        stateLabel(identity.state),
      );
    }
    return (
      <Link to={`/identidades?drawer=${agent.identity_id}`}>{parts.join(' · ')}</Link>
    );
  }
  if (agent.lost_identity_slug) {
    return <>{t('resources.agent.binding.lost', { slug: agent.lost_identity_slug })}</>;
  }
  return <>{t('resources.binding.none')}</>;
}

interface StateAlertsProps {
  resource: Resource;
  agent: ResourceAgent;
  canSync: boolean;
  canRelink: boolean;
  canDelete: boolean;
  syncing: boolean;
  relinking: boolean;
  onRecheck: () => void;
  onConvert: () => void;
  onRelink: (identityId: string, slug: string) => Promise<void>;
  onRemove: () => void;
}

/** Os avisos que o estado do agente pede, no topo da gaveta (pranchas aprovadas). */
function StateAlerts(props: StateAlertsProps) {
  const { agent } = props;
  if (agent.scope === 'external') {
    const schemes = agent.card?.security_schemes ?? [];
    return (
      <>
        {agent.state === 'no_route' ? (
          <Alert tone="warning" role="status" title={agentState('no_route').rotulo}>
            {t('resources.agent.noRoute.external', { api: apiOf(agent) })}
          </Alert>
        ) : null}
        {schemes.length > 0 && agent.route?.credential_configured === false ? (
          <Alert
            tone="warning"
            role="status"
            title={t('resources.agent.credential.title', { schemes: schemes.join(', ') })}
          >
            {t('resources.agent.credential.text', { api: apiOf(agent) })}
          </Alert>
        ) : null}
      </>
    );
  }
  if (agent.state === 'untrusted_target') return <UntrustedAlert {...props} />;
  if (agent.state === 'no_route') return <MissingRouteAlert {...props} />;
  return null;
}

/** «Sem destino confiável» (D8): o PDP nega, quem chamava guarda a delegação, e há três saídas. */
function UntrustedAlert({
  resource,
  agent,
  canSync,
  canRelink,
  canDelete,
  syncing,
  relinking,
  onConvert,
  onRelink,
  onRemove,
}: StateAlertsProps) {
  const identities = useIdentities(true);
  const [chosen, setChosen] = useState<string | null>(null);
  const [relinkError, setRelinkError] = useState<ApiError | null>(null);
  const callers = agent.invokers?.length ?? 0;
  // Só as identidades aprovadas DO MESMO SITE: a rota é lida pelo executor do site do agente.
  const options = useMemo(
    () =>
      (identities.data ?? [])
        .filter((identity) => identity.slug && identity.site_id === resource.site_id)
        .map((identity) => ({ value: identity.id, label: identity.slug ?? identity.id, mono: true })),
    [identities.data, resource.site_id],
  );

  const title = agent.lost_identity_slug
    ? t('resources.agent.untrusted.title.lost', {
        slug: agent.lost_identity_slug,
        when: dataHour(agent.identity_lost_at) ?? '—',
      })
    : t('resources.agent.untrusted.title.unlinked');
  const callersText =
    callers === 0
      ? t('resources.agent.untrusted.callers.zero')
      : t(
          callers === 1 ? 'resources.agent.untrusted.callers.one' : 'resources.agent.untrusted.callers.other',
          { n: callers },
        );

  return (
    <div data-testid="agent-untrusted">
      <Alert tone="danger" role="status" title={title}>
        {t('resources.agent.untrusted.text', { api: apiOf(agent) })} {callersText}{' '}
        {t('resources.agent.untrusted.choose')}
      </Alert>
      <div className={s.actionBlocks}>
        {canRelink ? (
          <section className={s.actionBlock} aria-labelledby="agent-relink-title">
            <h4 id="agent-relink-title" className={s.sectionTitle}>
              {t('resources.agent.relink.title')}
            </h4>
            <p className={s.note}>{t('resources.agent.relink.text')}</p>
            <div className={s.actionRow}>
              <SearchCombobox
                label={t('resources.agent.relink.label')}
                placeholder={t('resources.agent.relink.placeholder')}
                searchPlaceholder={t('resources.agent.relink.searchPlaceholder')}
                size="sm"
                options={options}
                value={chosen}
                onChange={(value) => setChosen((value as string | null) || null)}
              />
              <LoadingButton
                variant="primary"
                disabled={!chosen}
                loading={relinking}
                loadingLabel={t('resources.agent.relink.loading')}
                onClick={() => {
                  if (!chosen) return;
                  const slug = options.find((option) => option.value === chosen)?.label ?? '';
                  setRelinkError(null);
                  onRelink(chosen, slug).catch((error: unknown) => setRelinkError(error as ApiError));
                }}
              >
                {t('resources.agent.relink.action')}
              </LoadingButton>
            </div>
            {relinkError ? (
              <ErrorBanner error={relinkError} escrita title={t('resources.agent.relink.error')} />
            ) : null}
          </section>
        ) : null}
        {canSync ? (
          <section className={s.actionBlock} aria-labelledby="agent-convert-title">
            <h4 id="agent-convert-title" className={s.sectionTitle}>
              {t('resources.agent.convert.title')}
            </h4>
            <p className={s.note}>
              {t('resources.agent.convert.text', { api: apiOf(agent) })}
            </p>
            <LoadingButton
              variant="out"
              loading={syncing}
              loadingLabel={t('resources.agent.card.reread.loading')}
              onClick={onConvert}
            >
              {t('resources.agent.convert.action')}
            </LoadingButton>
          </section>
        ) : null}
        {canDelete ? (
          <section className={s.actionBlock} aria-labelledby="agent-remove-title">
            <h4 id="agent-remove-title" className={s.sectionTitle}>
              {t('resources.agent.remove.title')}
            </h4>
            <p className={s.note}>{removalText(resource.name, callers)}</p>
            <Button variant="danger" icon="trash" onClick={onRemove}>
              {t('resources.agent.remove.action')}
            </Button>
          </section>
        ) : null}
      </div>
    </div>
  );
}

/**
 * «Sem rota» de um agente interno: o gateway não tem a API `a2a/<caminho>`. O console só lê o gateway; quem cadastra
 * a API é o cliente, pelo processo dele (decisão 9). A sugestão de rota da borda saiu sem substituto (D6, decisão A
 * do M2): ficam o aviso, «Conferir de novo» e «Remover».
 */
// O servidor só remove um agente sem destino confiável ou sem rota (decisão 21): os dois avisos são exatamente esses
// estados, e por isso «Remover» mora dentro deles e em nenhum outro lugar da gaveta.
function MissingRouteAlert({ resource, agent, canSync, canDelete, syncing, onRecheck, onRemove }: StateAlertsProps) {
  return (
    <div data-testid="agent-missing-route">
      <Alert tone="warning" role="status" title={t('resources.agent.noRoute.title', { name: resource.name })}>
        {t('resources.agent.noRoute.text', { api: apiOf(agent) })}
      </Alert>
      <div className={s.actionRow}>
        {canSync ? (
          <LoadingButton
            variant="out"
            loading={syncing}
            loadingLabel={t('resources.agent.card.reread.loading')}
            onClick={onRecheck}
          >
            {t('resources.agent.noRoute.recheck')}
          </LoadingButton>
        ) : null}
        <span className={s.sub} data-testid="agent-missing-route-read-at">
          {t('resources.agent.noRoute.lastRead', { when: dataHour(agent.route_read_at) ?? '—' })}
        </span>
        {canDelete ? (
          <Button variant="danger" icon="trash" onClick={onRemove}>
            {t('resources.agent.remove.action')}
          </Button>
        ) : null}
      </div>
      <p className={s.note}>{t('resources.agent.noRoute.labelNote')}</p>
    </div>
  );
}

/** A secção «Card» do detalhe: o que o gateway leu do card, e «Reler o card». */
function CardSection({
  agent,
  canSync,
  syncing,
  onReread,
}: {
  agent: ResourceAgent;
  canSync: boolean;
  syncing: boolean;
  onReread: () => void;
}) {
  const card = agent.card;
  const transports = (card?.interfaces ?? []).map((item) => item.transport);
  const others = transports.filter((transport) => transport !== 'JSONRPC');
  const features = [
    card?.streaming ? t('resources.agent.card.features.streaming') : null,
    card?.extended_card ? t('resources.agent.card.features.extended') : null,
  ].filter(Boolean);
  const schemes = card?.security_schemes ?? [];
  const credential =
    schemes.length === 0
      ? t('resources.agent.card.credential.notRequired')
      : agent.route?.credential_configured
        ? t('resources.agent.card.credential.configured')
        : t('resources.agent.card.credential.missing');

  return (
    <section aria-labelledby="agent-card-title">
      <div className={s.sectionHead}>
        <h3 id="agent-card-title" className={s.sectionTitle}>
          {t('resources.agent.section.card')}
        </h3>
        {canSync ? (
          <LoadingButton
            variant="out"
            size="sm"
            loading={syncing}
            loadingLabel={t('resources.agent.card.reread.loading')}
            onClick={onReread}
          >
            {t('resources.agent.card.reread')}
          </LoadingButton>
        ) : null}
      </div>
      {agent.card_error ? (
        <Alert tone="warning" role="status" title={t('resources.agent.card.error.title')}>
          {cardErrorText(agent.card_error)}
        </Alert>
      ) : null}
      {!card ? (
        <p className={s.vazio}>{t('resources.agent.card.none')}</p>
      ) : (
        <dl className={s.readout}>
          <dt>{t('resources.agent.card.address')}</dt>
          <dd className={s.mono}>{`${agentPath(agent.path)}.well-known/agent-card.json`}</dd>
          <dt>{t('resources.agent.card.readAt')}</dt>
          <dd>{dataHour(agent.card_read_at) ?? t('resources.agent.card.readAt.never')}</dd>
          <dt>{t('resources.agent.card.version')}</dt>
          <dd>
            {card.protocol_version
              ? t('resources.agent.card.version.value', { version: card.protocol_version })
              : '—'}
          </dd>
          <dt>{t('resources.agent.card.interface')}</dt>
          <dd>
            {t('resources.agent.card.interface.jsonrpc')}
            {others.length > 0
              ? ` · ${t('resources.agent.card.interface.others', { list: others.join(', ') })}`
              : ''}
          </dd>
          {agent.scope === 'external' ? (
            <>
              <dt>{t('resources.agent.card.destination')}</dt>
              <dd>
                <span className={s.mono}>{agent.route?.endpoint ?? '—'}</span>{' '}
                <span className={s.sub}>{t('resources.agent.card.destination.help')}</span>
              </dd>
              <dt>{t('resources.agent.card.provider')}</dt>
              <dd>{card.provider || '—'}</dd>
            </>
          ) : (
            <>
              <dt>{t('resources.agent.card.announced')}</dt>
              <dd className={s.mono}>{card.url || '—'}</dd>
              <dt>{t('resources.agent.card.features')}</dt>
              <dd>{features.length ? features.join(' · ') : t('resources.agent.card.features.none')}</dd>
            </>
          )}
          <dt>{t('resources.agent.card.security')}</dt>
          <dd>{schemes.length ? schemes.join(', ') : t('resources.agent.card.security.none')}</dd>
          <dt>{t('resources.agent.card.credential')}</dt>
          <dd>{credential}</dd>
        </dl>
      )}
    </section>
  );
}

/**
 * A secção «API no gateway»: a declaração do agente interno, a API `a2a/<caminho>` lida do gateway. O externo não
 * tem: o gateway chama o endereço da API direto.
 */
function RouteSection({ agent }: { agent: ResourceAgent }) {
  const route = agent.route;
  return (
    <section aria-labelledby="agent-route-title" data-testid="agent-route">
      <h3 id="agent-route-title" className={s.sectionTitle}>
        {t('resources.agent.section.route')}
      </h3>
      {agent.scope === 'external' ? (
        <p className={s.note} data-testid="agent-route-external">
          {t('resources.agent.route.external')}
        </p>
      ) : (
        <>
          <p className={s.note}>{t('resources.agent.route.intro')}</p>
          {route?.conflict ? (
            <Alert tone="warning" role="status">
              <span data-testid="agent-route-conflict">{t('resources.agent.route.conflict', { api: apiOf(agent) })}</span>
            </Alert>
          ) : null}
          <dl className={s.readout}>
            <dt>{t('resources.agent.route.route')}</dt>
            <dd className={s.mono} data-testid="agent-route-api">
              {apiOf(agent)}
            </dd>
            <dt>{t('resources.agent.route.destination')}</dt>
            <dd>
              {route?.host ? (
                <>
                  <span className={s.mono}>{route.port ? `${route.host}:${route.port}` : route.host}</span>{' '}
                  <span className={s.sub}>{t('resources.agent.route.destination.readIn', { api: apiOf(agent) })}</span>
                </>
              ) : (
                <span className={s.vazio}>—</span>
              )}
            </dd>
            <dt>{t('resources.agent.route.pods')}</dt>
            <dd>
              {route?.pods_total === undefined
                ? '—'
                : route.mixed
                  ? t('resources.agent.route.pods.mixed', { n: route.pods_total })
                  : route.application
                    ? t('resources.agent.route.pods.of', { n: route.pods_total, slug: route.application })
                    : String(route.pods_total)}
            </dd>
            <dt>{t('resources.agent.route.readAt')}</dt>
            <dd>{dataHour(agent.route_read_at) ?? '—'}</dd>
          </dl>
        </>
      )}
    </section>
  );
}

/** A aba «Skills»: o que o card declara. A autorização é pelo agente inteiro. */
function SkillsTab({ agent }: { agent: ResourceAgent }) {
  const skills = agent.card?.skills ?? [];
  const columns: DataTableColumn<(typeof skills)[number]>[] = [
    { key: 'skill', header: t('resources.agent.skills.col.skill'), cell: (skill) => <b>{skill.name}</b> },
    {
      key: 'description',
      header: t('resources.agent.skills.col.description'),
      cell: (skill) => skill.description || <span className={s.vazio}>—</span>,
    },
    {
      key: 'tags',
      header: t('resources.agent.skills.col.tags'),
      cell: (skill) => (skill.tags?.length ? skill.tags.join(' · ') : <span className={s.vazio}>—</span>),
    },
  ];
  return (
    <>
      <DataTable
        label={t('resources.agent.skills.label')}
        columns={columns}
        rows={skills}
        rowKey={(skill) => skill.id}
        emptyIcon="robot"
        emptyTitle={t('resources.agent.skills.empty')}
      />
      <p className={s.note}>{t('resources.agent.skills.note')}</p>
    </>
  );
}

/**
 * A aba «Quem pode invocar»: pelos contratos de quem chama (eixo identidade) e as regras de Human Access que
 * afirmam a delegação (eixo pessoa). Substitui o card «Agentes como destino» de T6 (D2).
 */
function InvokersTab({ invokers, multiSite }: { invokers: AgentInvoker[]; multiSite: boolean }) {
  const columns: DataTableColumn<AgentInvoker>[] = [
    { key: 'identity', header: t('resources.agent.invokers.col.identity'), cell: (row) => <b>{row.slug}</b> },
    ...(multiSite
      ? [
          {
            key: 'site',
            header: t('resources.agent.invokers.col.site'),
            cell: (row: AgentInvoker) => (row.site_slug ? <Chip>{row.site_slug}</Chip> : '—'),
          } satisfies DataTableColumn<AgentInvoker>,
        ]
      : []),
    {
      key: 'contract',
      header: t('resources.agent.invokers.col.contract'),
      cell: (row) => `v${row.contract_version}`,
    },
    {
      key: 'rule',
      header: t('resources.agent.invokers.col.rule'),
      cell: (row) =>
        row.affirmed_by.length ? (
          row.affirmed_by.join(', ')
        ) : (
          <Badge tone="warning">{t('resources.agent.invokers.noRule')}</Badge>
        ),
    },
    {
      key: 'action',
      header: t('resources.agent.invokers.col.action'),
      cell: (row) => (
        <Link
          to={identityAccessHref(row.identity_id, 'outgoing')}
          aria-label={t('resources.agent.invokers.action.label', { slug: row.slug })}
        >
          {t('resources.agent.invokers.action')}
        </Link>
      ),
    },
  ];
  return (
    <>
      <DataTable
        label={t('resources.agent.invokers.label')}
        columns={columns}
        rows={invokers}
        rowKey={(row) => row.identity_id}
        emptyIcon="robot"
        emptyTitle={t('resources.agent.invokers.empty')}
      />
      <p className={s.note}>{t('resources.agent.invokers.note')}</p>
    </>
  );
}
